/**
 * GuestReadinessBlock — si el huésped que llega hoy está preparado.
 *
 * Una fila por huésped y una sola frase: qué le falta. En escritorio esa fila
 * cabe en una línea (nombre · unidad | qué falta | Abrir); en móvil se apila en
 * dos, que es lo que pide una pantalla estrecha. La lista de campos se
 * despliega solo si se pide, y quien ya está listo se aparta detrás del filtro
 * que hay junto al título.
 *
 * Solo ESTADO. Ni un número de documento, ni una imagen: el contenido del
 * pasaporte vive en su propio ámbito de ficheros y no se enseña en una
 * pantalla operativa (§7.3).
 *
 * Los campos que se piden son exactamente los que la propiedad marcó como
 * obligatorios en Ajustes › Requisitos del huésped, y la fila de inmigración
 * solo existe si tiene activado el reporte de extranjeros.
 */
import React, { useState } from "react";
import { Check, X, CaretDown } from "@phosphor-icons/react";
import { GUEST_FIELD_LABELS } from "@/data/settingsV2Demo";
import { PRE_CHECKIN_LABEL, IMMIGRATION_LABEL } from "@/data/todayV2Demo";
import { readinessSummary } from "@/lib/todayV2Selectors";
import {
  TodayBlock, TodayCard, RowList, Row, StatusPill, QuickAction, EmptyState,
} from "@/pages/admin/pms/v2/V2Primitives";

export default function GuestReadinessBlock({ readiness }) {
  const [showAll, setShowAll] = useState(false);
  const pending = readiness.filter((r) => !r.ready);
  const ready = readiness.filter((r) => r.ready);
  const visible = showAll ? [...pending, ...ready] : pending;

  return (
    <TodayBlock
      id="today-readiness"
      title="Preparación del huésped"
      count={pending.length}
      testId="today-readiness"
      action={ready.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          aria-pressed={showAll}
          data-testid="today-readiness-more"
          className="-my-2 shrink-0 min-h-[44px] inline-flex items-center text-[11px] text-stone-500 hover:text-stone-900 dark:hover:text-stone-100"
        >
          {showAll ? "Solo pendientes" : `Ver todos (${readiness.length})`}
        </button>
      ) : null}
    >
      {visible.length === 0 ? (
        <EmptyState testId="today-readiness-empty">
          Todas las llegadas de hoy están completas.
        </EmptyState>
      ) : (
        <TodayCard padding="p-0">
          <RowList testId="today-readiness-list">
            {visible.map((row) => (
              <ReadinessRow key={row.reservation.reservation_id} row={row} />
            ))}
          </RowList>
        </TodayCard>
      )}
    </TodayBlock>
  );
}

function ReadinessRow({ row }) {
  const [open, setOpen] = useState(false);
  const r = row.reservation;
  const summary = readinessSummary(row);

  return (
    <Row testId={`readiness-${r.reservation_id}`} className="lg:py-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid={`readiness-toggle-${r.reservation_id}`}
          className="min-w-0 flex-1 text-left min-h-[44px] flex items-center gap-2"
        >
          <span className="min-w-0 flex-1 flex flex-col lg:flex-row lg:items-baseline lg:gap-3">
            <span className="truncate text-sm text-stone-900 dark:text-stone-100 lg:w-40 lg:shrink-0">
              {r.guest.name}
              <span className="text-stone-400"> · {row.unit.villa_id}</span>
            </span>
            <span className={`truncate text-[11px] lg:flex-1 ${
              row.ready
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-amber-700 dark:text-amber-300"
            }`}>
              {row.ready ? `✓ ${summary}` : summary}
            </span>
          </span>
          <CaretDown
            size={13}
            className={`shrink-0 text-stone-300 dark:text-stone-600 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        <div className="shrink-0">
          <QuickAction
            label="Abrir"
            to={`/pms/reservations/${r.reservation_id}`}
            testId={`readiness-open-${r.reservation_id}`}
          />
        </div>
      </div>

      {open && (
        <div className="mt-1.5 mb-1 pl-1" data-testid={`readiness-detail-${r.reservation_id}`}>
          <div className="text-[11px] text-stone-400 mb-1">
            Check-in online: {PRE_CHECKIN_LABEL[row.pre_checkin]}
          </div>
          <ul className="space-y-1" data-testid={`readiness-missing-${r.reservation_id}`}>
            {/* Solo lo que FALTA. Enumerar también lo que ya está hecho
                convertía esto en la lista de 14 casillas que Hoy no quiere. */}
            {row.missing.map((m) => (
              <li key={`${m.field}-${m.guest_id || "party"}`} className="flex items-center gap-2 text-xs">
                <Mark done={false} />
                <span className="text-amber-700 dark:text-amber-300">
                  {GUEST_FIELD_LABELS[m.field] || m.field}
                  {m.guest_name ? <span className="text-stone-400"> · {m.guest_name}</span> : null}
                </span>
              </li>
            ))}
            {row.missing.length === 0 && (
              <li className="flex items-center gap-2 text-xs">
                <Mark done />
                <span className="text-stone-600 dark:text-stone-300">
                  Datos del huésped completos
                </span>
              </li>
            )}
            {row.immigration_tracked && (
              <li className="flex items-center gap-2 text-xs" data-testid={`readiness-immigration-${r.reservation_id}`}>
                <Mark done={row.immigration !== "due"} />
                <span className={row.immigration === "due"
                  ? "text-amber-700 dark:text-amber-300"
                  : "text-stone-600 dark:text-stone-300"}>
                  Reporte de inmigración (APOA): {IMMIGRATION_LABEL[row.immigration].toLowerCase()}
                </span>
              </li>
            )}
          </ul>
          {/* Lo que cambia cómo se prepara la villa: alergias, minibar sin
              alcohol, traslado. Nunca el motivo de nadie. */}
          {row.operational_notes?.length > 0 && (
            <ul className="mt-1.5 space-y-0.5" data-testid={`readiness-notes-${r.reservation_id}`}>
              {row.operational_notes.map((n) => (
                <li key={n.key} className="text-[11px]">
                  <span className="text-stone-400">{n.label}: </span>
                  <span className={n.tone === "red"
                    ? "text-red-700 dark:text-red-300"
                    : "text-stone-600 dark:text-stone-300"}>{n.value}</span>
                </li>
              ))}
            </ul>
          )}
          {row.ready && (
            <div className="mt-2">
              <StatusPill tone="emerald">Preparado</StatusPill>
            </div>
          )}
        </div>
      )}
    </Row>
  );
}

function Mark({ done }) {
  return done
    ? <Check size={12} weight="bold" className="text-emerald-600 dark:text-emerald-400 shrink-0" />
    : <X size={12} weight="bold" className="text-amber-600 dark:text-amber-400 shrink-0" />;
}
